import { inject, Injectable, signal } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../../environments/environment'
import {
    ExportProgressVm,
    DevicesExportRequest,
    SoftwaresExportRequest,
    ServicesExportRequest,
    InstallazioniSoftwareExportRequest,
    FornitoriExportRequest,
} from './models/export.vm'

export type ExportStatus = 'idle' | 'running' | 'complete' | 'error'

@Injectable({ providedIn: 'root' })
export class ExportService {
    private http = inject(HttpClient)
    private readonly base = `${environment.apiUrl}/api/v2/Export`

    // ── State (read by ExportToastComponent) ─────────────────────────────────
    readonly status = signal<ExportStatus>('idle')
    readonly percent = signal(0)
    readonly message = signal('')
    readonly exportId = signal<string | null>(null)
    readonly fileName = signal<string | null>(null)
    readonly jobId = signal<string | null>(null)
    readonly isVisible = signal(false)
    readonly emailSent = signal(false)
    readonly fileDownloaded = signal(false)

    private eventSource: EventSource | null = null

    // ── Public API ────────────────────────────────────────────────────────────

    startExport(emptyTemplate: boolean): void {
        // Close any previous export
        this.closeEventSource()
        this.reset()
        this.isVisible.set(true)
        this.status.set('running')

        this.http.post<{ jobId: string }>(`${this.base}/Initialize`, { emptyTemplate }).subscribe({
            next: ({ jobId }) => {
                this.jobId.set(jobId)
                this.openStream(jobId)
            },
            error: () => {
                this.status.set('error')
                this.message.set('EXPORT.TOAST.ERROR_INIT')
            },
        })
    }

    /**
     * Parametric device export: only the visible columns and only the rows matching the current
     * search/filters (or the explicitly selected rows). Reuses the same SSE progress/download flow.
     */
    startDevicesTableExport(payload: DevicesExportRequest): void {
        this.closeEventSource()
        this.reset()
        this.isVisible.set(true)
        this.status.set('running')

        this.http
            .post<{ jobId: string }>(`${this.base}/InitializeDevicesExport`, payload)
            .subscribe({
                next: ({ jobId }) => {
                    this.jobId.set(jobId)
                    this.openStream(jobId)
                },
                error: () => {
                    this.status.set('error')
                    this.message.set('EXPORT.TOAST.ERROR_INIT')
                },
            })
    }

    /**
     * Parametric software export: only the visible columns and only the rows matching the current
     * search/filters (or the explicitly selected rows). Reuses the same SSE progress/download flow.
     */
    startSoftwaresTableExport(payload: SoftwaresExportRequest): void {
        this.closeEventSource()
        this.reset()
        this.isVisible.set(true)
        this.status.set('running')

        this.http
            .post<{ jobId: string }>(`${this.base}/InitializeSoftwaresExport`, payload)
            .subscribe({
                next: ({ jobId }) => {
                    this.jobId.set(jobId)
                    this.openStream(jobId)
                },
                error: () => {
                    this.status.set('error')
                    this.message.set('EXPORT.TOAST.ERROR_INIT')
                },
            })
    }

    /**
     * Parametric services export: only the visible columns and only the rows matching the current
     * search/filters (or the explicitly selected rows). Reuses the same SSE progress/download flow.
     */
    startServicesTableExport(payload: ServicesExportRequest): void {
        this.closeEventSource()
        this.reset()
        this.isVisible.set(true)
        this.status.set('running')

        this.http
            .post<{ jobId: string }>(`${this.base}/InitializeServicesExport`, payload)
            .subscribe({
                next: ({ jobId }) => {
                    this.jobId.set(jobId)
                    this.openStream(jobId)
                },
                error: () => {
                    this.status.set('error')
                    this.message.set('EXPORT.TOAST.ERROR_INIT')
                },
            })
    }

    /**
     * Parametric installazioni-software export: only the visible columns and only the explicitly
     * selected rows (this grid has no server-side search/filters to mirror, only pagination — see
     * ExportInstallazioniSoftwareTable). Reuses the same SSE progress/download flow.
     */
    startInstallazioniSoftwareTableExport(payload: InstallazioniSoftwareExportRequest): void {
        this.closeEventSource()
        this.reset()
        this.isVisible.set(true)
        this.status.set('running')

        this.http
            .post<{ jobId: string }>(`${this.base}/InitializeInstallazioniSoftwareExport`, payload)
            .subscribe({
                next: ({ jobId }) => {
                    this.jobId.set(jobId)
                    this.openStream(jobId)
                },
                error: () => {
                    this.status.set('error')
                    this.message.set('EXPORT.TOAST.ERROR_INIT')
                },
            })
    }

    /**
     * Parametric fornitori (anagrafica) export: only the visible columns and only the rows matching
     * the current search (sort/per-column filters aren't honored server-side for fornitori yet — see
     * FornitoriMuaApiService.Read) or the explicitly selected rows. Reuses the same SSE progress/download flow.
     */
    startFornitoriTableExport(payload: FornitoriExportRequest): void {
        this.closeEventSource()
        this.reset()
        this.isVisible.set(true)
        this.status.set('running')

        this.http
            .post<{ jobId: string }>(`${this.base}/InitializeFornitoriExport`, payload)
            .subscribe({
                next: ({ jobId }) => {
                    this.jobId.set(jobId)
                    this.openStream(jobId)
                },
                error: () => {
                    this.status.set('error')
                    this.message.set('EXPORT.TOAST.ERROR_INIT')
                },
            })
    }

    /** Called when the user requests email while export is still running. */
    requestEmailForRunning(): void {
        const jid = this.jobId()
        if (!jid) return
        this.http.post(`${this.base}/RequestEmail`, { jobId: jid }).subscribe()
    }

    /** Called when the user requests email for an already-completed export. */
    requestEmailForCompleted(): void {
        const eid = this.exportId()
        if (!eid) return
        this.http.post(`${this.base}/RequestEmail`, { exportId: eid }).subscribe()
    }

    downloadExport(): void {
        const eid = this.exportId()
        const name = this.fileName()
        if (!eid || !name) return

        this.http
            .get(`${this.base}/Download/${eid}`, { responseType: 'blob' })
            .subscribe((blob) => {
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = name
                a.click()
                URL.revokeObjectURL(url)
                this.fileDownloaded.set(true)
            })
    }

    hide(): void {
        this.closeEventSource()
        this.isVisible.set(false)
    }

    // ── Private ───────────────────────────────────────────────────────────────

    private openStream(jobId: string): void {
        const source = new EventSource(`${this.base}/Stream/${jobId}`)
        this.eventSource = source

        source.onmessage = (event) => {
            try {
                const data: ExportProgressVm = JSON.parse(event.data)
                if (data.type === 'progress') {
                    this.percent.set(data.percent)
                    this.message.set(data.message ?? '')
                } else if (data.type === 'complete') {
                    this.percent.set(100)
                    this.status.set('complete')
                    this.exportId.set(data.exportId ?? null)
                    this.fileName.set(data.fileName ?? null)
                    source.close()
                    this.eventSource = null
                } else if (data.type === 'error') {
                    this.status.set('error')
                    this.message.set(data.message ?? '')
                    source.close()
                    this.eventSource = null
                }
            } catch {
                /* ignore malformed event */
            }
        }

        source.onerror = () => {
            // SSE connection dropped (client navigated away or server issue).
            // The server keeps running — we just stop listening.
            source.close()
            this.eventSource = null
        }
    }

    private closeEventSource(): void {
        this.eventSource?.close()
        this.eventSource = null
    }

    private reset(): void {
        this.status.set('idle')
        this.percent.set(0)
        this.message.set('')
        this.exportId.set(null)
        this.fileName.set(null)
        this.jobId.set(null)
        this.emailSent.set(false)
        this.fileDownloaded.set(false)
    }
}
